import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useClub } from '../../app/club';
import { useRepo } from '../../app/services';
import { useAccessRules, useClubMutation, useCredentials, useGroupMembers, useGroups, usePersonAppStatus, usePersons } from '../../data/hooks';
import { Badge, Button, Dialog, EmptyState, ErrorState, Field, Loading, PageHeader, Panel, useToast } from '../../components/ui';
import { AppStatusBadge, BulkInviteDialog } from '../../components/AppInvite';
import { appStatus, needsInvite } from '../../lib/appStatus';
import type { Person } from '../../lib/types';
import { ExpiryChoiceField } from '../../components/ExpiryChoiceField';
import { computeScope, inScope } from '../../app/scope';
import { can } from '../../lib/roles';
import { fmtDate } from '../../lib/format';
import { resolveExpiry, UNSET_EXPIRY, type ExpiryChoice } from '../../lib/expiry';
import { Pager } from './LogbookPage';
import { errorText } from '../../lib/errors';

const PAGE = 25;

export function PersonsPage() {
  const { orgId, role, managedGroupIds, readOnly } = useClub();
  const [params, setParams] = useSearchParams();
  const persons = usePersons(orgId);
  const groups = useGroups(orgId);
  const members = useGroupMembers(orgId);
  const rules = useAccessRules(orgId);
  const credentials = useCredentials(orgId);
  const appRows = usePersonAppStatus(orgId);
  const [bulkInvite, setBulkInvite] = useState(false);
  const [query, setQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [special, setSpecial] = useState<'' | 'no_app' | 'expiring'>('');
  const [page, setPage] = useState(0);
  const creating = params.get('nieuw') === '1';

  const scope = useMemo(
    () => (members.data && rules.data ? computeScope(role, managedGroupIds, members.data, rules.data) : null),
    [role, managedGroupIds, members.data, rules.data],
  );

  const qs = [persons, groups, members, rules];
  if (qs.some((q) => q.isLoading)) return <Loading />;
  const err = qs.find((q) => q.error)?.error;
  if (err) return <ErrorState error={err} />;

  const horizon = Date.now() + 30 * 86400_000;
  const groupName = new Map(groups.data!.map((g) => [g.id, g.name]));
  const membershipsOf = (pid: string) => members.data!.filter((m) => m.person_id === pid);
  const appRowById = new Map((appRows.data ?? []).map((r) => [r.person_id, r]));
  const statusOf = (p: Person) => appStatus(p, appRowById.get(p.id));
  const q = query.trim().toLowerCase();
  const rows = persons
    .data!.filter((p) => inScope(scope, 'personIds', p.id) && !p.is_loan_card)
    .filter((p) => !q || p.full_name.toLowerCase().includes(q) || p.email?.includes(q) || p.external_ref?.toLowerCase().includes(q))
    .filter((p) => !groupFilter || membershipsOf(p.id).some((m) => m.group_id === groupFilter))
    .filter((p) => special !== 'no_app' || statusOf(p).kind !== 'active')
    .filter(
      (p) =>
        special !== 'expiring' ||
        membershipsOf(p.id).some((m) => m.valid_until && new Date(m.valid_until).getTime() < horizon),
    )
    .sort((a, b) => a.full_name.localeCompare(b.full_name, 'nl'));
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const visible = rows.slice(page * PAGE, page * PAGE + PAGE);
  const visibleGroups = groups.data!.filter((g) => inScope(scope, 'groupIds', g.id));
  // Bulk: alle zichtbare personen (huidige zoek-/groepfilter) zonder app en zonder lopende uitnodiging.
  const inviteCandidates = rows.filter((p) => needsInvite(statusOf(p)));
  const canInvite = can('person.invite', role) && !readOnly;

  const setCreating = (on: boolean) => {
    const p = new URLSearchParams(params);
    if (on) p.set('nieuw', '1');
    else p.delete('nieuw');
    setParams(p, { replace: true });
  };

  return (
    <div className="stack-lg">
      <PageHeader
        title="Personen"
        intro={role === 'group_manager' ? 'De leden van de groepen die jij beheert.' : 'Iedereen die bij jullie club een pas, vingerafdruk, code of de app gebruikt.'}
        actions={
          <>
            {canInvite && (
              <Button onClick={() => setBulkInvite(true)} disabled={!appRows.data}>
                Leden zonder app uitnodigen
              </Button>
            )}
            {can('person.create', role) && (
              <Button variant="primary" onClick={() => setCreating(true)}>
                Persoon toevoegen
              </Button>
            )}
          </>
        }
      />
      <Panel>
        <div className="toolbar">
          <div className="grow">
            <Field label="Zoeken">
              {(p) => (
                <input {...p} className="input" type="search" placeholder="Naam, e-mail of relatienummer" value={query} onChange={(e) => { setQuery(e.target.value); setPage(0); }} />
              )}
            </Field>
          </div>
          <Field label="Groep">
            {(p) => (
              <select {...p} className="input" value={groupFilter} onChange={(e) => { setGroupFilter(e.target.value); setPage(0); }}>
                <option value="">Alle groepen</option>
                {visibleGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Filter">
            {(p) => (
              <select {...p} className="input" value={special} onChange={(e) => { setSpecial(e.target.value as typeof special); setPage(0); }}>
                <option value="">Iedereen</option>
                <option value="no_app">Zonder app</option>
                <option value="expiring">Verloopt binnen 30 dagen</option>
              </select>
            )}
          </Field>
        </div>
        {rows.length === 0 ? (
          <EmptyState title={persons.data!.length ? 'Niemand gevonden' : 'Nog geen personen'}>
            {persons.data!.length ? 'Pas je zoekopdracht of filters aan.' : 'Importeer leden uit Sportlink of voeg iemand toe.'}
          </EmptyState>
        ) : (
          <>
            <div className="table-wrap">
              <table className="data stack-mobile">
                <thead>
                  <tr>
                    <th scope="col">Naam</th>
                    <th scope="col">Groepen</th>
                    <th scope="col">App</th>
                    <th scope="col">Middelen</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((p) => {
                    const ms = membershipsOf(p.id).filter((m) => inScope(scope, 'groupIds', m.group_id));
                    const creds = (credentials.data ?? []).filter((c) => c.person_id === p.id && c.status !== 'revoked');
                    return (
                      <tr key={p.id}>
                        <td className="cell-main">
                          <Link className="cell-title" to={`/c/${orgId}/personen/${p.id}`}>
                            {p.full_name}
                          </Link>
                          <span className="cell-sub">
                            {p.external_ref ?? 'Geen relatienummer'}
                            {p.is_minor ? ', minderjarig' : ''}
                          </span>
                        </td>
                        <td data-label="Groepen">
                          {ms.length === 0 ? (
                            <span className="muted">Geen</span>
                          ) : (
                            ms.map((m) => (
                              <span key={m.group_id} className="cell-sub" style={{ color: 'inherit' }}>
                                {groupName.get(m.group_id)}
                                {m.valid_until && <span className="muted"> tot {fmtDate(m.valid_until)}</span>}
                              </span>
                            ))
                          )}
                        </td>
                        <td data-label="App">
                          <AppStatusBadge status={statusOf(p)} />
                        </td>
                        <td data-label="Middelen">
                          {creds.some((c) => c.status === 'revoke_pending') ? (
                            <Badge tone="red">Intrekking wacht</Badge>
                          ) : (
                            <span className="muted small">{credLabel(creds.filter((c) => c.type !== 'app').length)}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager page={page} pages={pages} total={rows.length} onChange={setPage} />
          </>
        )}
      </Panel>
      {bulkInvite && (
        <BulkInviteDialog
          candidates={inviteCandidates}
          context={
            query || groupFilter || special
              ? 'Alleen de personen die nu in de lijst staan (zoeken en filters gelden).'
              : role === 'group_manager'
                ? 'Alle leden van jouw groepen die nog geen app hebben.'
                : 'Iedereen in de club die nog geen app heeft en niet al is uitgenodigd.'
          }
          onClose={() => setBulkInvite(false)}
        />
      )}
      {creating && <CreatePersonDialog onClose={() => setCreating(false)} groups={visibleGroups} requireGroup={role === 'group_manager'} />}
    </div>
  );
}

const personSchema = z.object({
  full_name: z.string().trim().min(2, 'Vul een naam in.'),
  email: z.union([z.literal(''), z.string().trim().email('E-mailadres klopt niet.')]),
  phone: z.union([z.literal(''), z.string().trim().regex(/^[+0-9 ()-]{6,20}$/, 'Telefoonnummer klopt niet.')]),
  external_ref: z.string().trim().max(64),
});

function CreatePersonDialog({ onClose, groups, requireGroup }: { onClose: () => void; groups: { id: string; name: string; valid_until: string | null }[]; requireGroup: boolean }) {
  const { orgId } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', external_ref: '', is_minor: false });
  const [groupId, setGroupId] = useState('');
  const [expiry, setExpiry] = useState<ExpiryChoice>(UNSET_EXPIRY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useClubMutation(orgId, async () => {
    const input = {
      full_name: form.full_name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      external_ref: form.external_ref.trim() || null,
      is_minor: form.is_minor,
    };
    if (groupId) {
      // In één stap aanmaken + in de groep: zo werkt het ook voor de groepsbeheerder (create_person_in_group).
      const res = resolveExpiry(expiry, { groupValidUntil: groups.find((g) => g.id === groupId)?.valid_until });
      return repo.createPersonInGroup(groupId, input, res.ok ? res.validUntil : null);
    }
    return repo.createPerson(orgId, input);
  });
  const group = groups.find((g) => g.id === groupId);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const parsed = personSchema.safeParse(form);
    if (!parsed.success) for (const i of parsed.error.issues) errs[String(i.path[0])] = i.message;
    if (requireGroup && !groupId) errs.group = 'Als groepsbeheerder voeg je iemand direct toe aan je eigen groep.';
    if (groupId) {
      const res = resolveExpiry(expiry, { groupValidUntil: group?.valid_until });
      if (!res.ok) errs.expiry = res.error;
    }
    setErrors(errs);
    if (Object.keys(errs).length) return;
    try {
      const p = await save.mutateAsync(undefined);
      toast(`${p.full_name} toegevoegd`);
      onClose();
      navigate(`/c/${orgId}/personen/${p.id}`);
    } catch (err) {
      setErrors({ form: errorText(err, 'Opslaan mislukt') });
    }
  };

  const input = (key: 'full_name' | 'email' | 'phone' | 'external_ref') => ({
    className: 'input',
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Persoon toevoegen" description="Een persoon heeft geen account nodig: een pas, vingerafdruk of code volstaat.">
      <form className="stack" onSubmit={submit} noValidate>
        <Field label="Naam" error={errors.full_name}>
          {(p) => <input {...p} {...input('full_name')} autoComplete="off" />}
        </Field>
        <Field label="E-mailadres" hint="Nodig voor de app-uitnodiging. Niet verplicht voor minderjarigen." error={errors.email}>
          {(p) => <input {...p} {...input('email')} type="email" />}
        </Field>
        <Field label="Telefoon" error={errors.phone}>
          {(p) => <input {...p} {...input('phone')} type="tel" />}
        </Field>
        <Field label="Relatienummer" hint="Bijvoorbeeld uit Sportlink. Gebruikt bij latere imports." error={errors.external_ref}>
          {(p) => <input {...p} {...input('external_ref')} />}
        </Field>
        <label className="row" style={{ gap: 8 }}>
          <input type="checkbox" checked={form.is_minor} onChange={(e) => setForm((f) => ({ ...f, is_minor: e.target.checked }))} style={{ width: 18, height: 18 }} />
          Minderjarig (beperkte gegevens, geen vingerafdruk zonder toestemming ouders)
        </label>
        <Field label={requireGroup ? 'Groep' : 'Groep (optioneel)'} error={errors.group}>
          {(p) => (
            <select {...p} className="input" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">{requireGroup ? 'Kies je groep' : 'Nog geen groep'}</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        {groupId && <ExpiryChoiceField value={expiry} onChange={setExpiry} error={errors.expiry} groupValidUntil={group?.valid_until} />}
        {errors.form && (
          <div className="alert alert-danger" role="alert">
            {errors.form}
          </div>
        )}
        <div className="dialog-actions">
          <Button onClick={onClose}>Annuleren</Button>
          <Button variant="primary" type="submit" disabled={save.isPending}>
            Persoon toevoegen
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** "Geen", "1 pas of vinger", "2 passen of vingers". */
function credLabel(n: number): string {
  return n === 0 ? 'Geen' : n === 1 ? '1 pas of vinger' : `${n} passen of vingers`;
}
